import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../api";
import MobileHeader from "../mobile/MobileHeader";
import { COLOR, FONT, RADIUS, SHADOW, ThemeProvider, GlobalMobileStyles } from "../mobile/mobiletheme";

export default function MobileGarlandOrder() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user_id = localStorage.getItem("phone") || "guest";
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [search, setSearch] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [neededDate, setNeededDate] = useState("");
  const [neededTime, setNeededTime] = useState("");
  const [neededAmPm, setNeededAmPm] = useState("AM");
  const [notes, setNotes] = useState("");
  const [headerHeight, setHeaderHeight] = useState(112);
  
  const [address, setAddress] = useState({
    fullName: "",
    phone: "",
    altPhone: "",
    building: "",
    landmark: "",
    street: "",
    city: "",
    postalCode: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const goSearch = () => {
    if (search.trim() !== "") navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return setError(t("mobile.validImage"));
    if (file.size > 5 * 1024 * 1024) return setError(t("mobile.image5mb"));
    setError(""); setPhoto(file); setPreview(URL.createObjectURL(file));
  };

  const handleAddressChange = (e) => {
    setAddress({ ...address, [e.target.name]: e.target.value });
  };

  const to24HourTime = (time12, ampm) => {
    const [hoursStr, minutesStr] = time12.split(":");
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10) || 0;
    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
  };

  // Auto-inserts the colon as the user types digits, so "1100" becomes "11:00"
  // instead of sitting there unformatted.
  const formatTimeInput = (raw) => {
    const digits = raw.replace(/[^0-9]/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const handleSubmit = async () => {
    if (!isLoggedIn || user_id === "guest") return setError(t("mobile.pleaseLogin"));
    if (!photo) return setError(t("mobile.attachPhoto"));
    if (!neededDate || !neededTime) return setError(t("mobile.specifyDateTime"));
    if (!/^([1-9]|1[0-2]):[0-5][0-9]$/.test(neededTime)) return setError(t("mobile.validTime"));
    
    // UPDATED VALIDATION: altPhone is now mandatory, postalCode is omitted
    if (!address.fullName || !address.phone || !address.altPhone || !address.building || !address.street || !address.city) {
      return setError(t("mobile.fillContactAddress"));
    }

    setError("");
    setSubmitting(true);
    const combinedNeededBy = `${neededDate} ${to24HourTime(neededTime, neededAmPm)}`;

    try {
      const formData = new FormData();
      formData.append("user_id", user_id);
      formData.append("reference_image", photo);
      formData.append("needed_by", combinedNeededBy);
      
      formData.append("notes", notes);
      formData.append("name", address.fullName);
      formData.append("phone_number", address.phone);
      formData.append("alt_phone_num", address.altPhone);
      formData.append("building_name", address.building);
      formData.append("street", address.street);
      formData.append("landmark", address.landmark);
      formData.append("city_or_village", address.city);
      formData.append("pin_code", address.postalCode);
      formData.append("email", user_id); 
      formData.append("state", "Telangana");
      
      formData.append("price", "0"); 
      formData.append("category", "garland");

      const res = await axios.post(`${API_URL}/garland`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        setSubmitted(true);
      } else {
        setError(res.data.message || t("mobile.somethingWentWrong"));
      }
    } catch (err) {
      setError(err.response?.data?.message || t("mobile.failedSubmitRequest"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider>
      <GlobalMobileStyles />
    <div style={styles.page}>
      <MobileHeader
        searchValue={search}
        setSearchValue={setSearch}
        onSearch={goSearch}
        showLogo={false}
        showTitleBar
        showBackButton={true}
        onBack={() => navigate(-1)}
        title={submitted ? t("mobile.garlandRequestTitle") : t("mobile.garlandOrderTitle")}
        activeTab="home"
        isLoggedIn={isLoggedIn}
        onHeightChange={setHeaderHeight}
      />

      <div style={{ ...styles.scrollArea, paddingTop: `${headerHeight + 15}px` }}>
        {!isLoggedIn || user_id === "guest" ? (
          <div style={styles.confirmCard}>
            <h2 style={{ color: COLOR.ink }}> {t("mobile.loginRequired")} </h2>
            <p style={styles.confirmText}>
              You must be logged in to place a garland request. Please return to the homepage to authenticate.
            </p>
            <button style={styles.submitBtn} onClick={() => navigate("/")}> {t("mobile.goHomeLogin")} </button>
          </div>
        ) : submitted ? (
          <div style={styles.confirmCard}>
            <div style={styles.confirmIcon}>✓</div>
            <h2 style={{ color: COLOR.ink }}> {t("mobile.requestReceived")} </h2>
            <p style={styles.confirmText}> {t("mobile.garlandConfirm")} </p>
            <button style={styles.submitBtn} onClick={() => navigate("/")}> {t("mobile.backHome")} </button>
          </div>
        ) : (
          <div style={styles.card}>
            <p style={styles.subheading}> {t("mobile.garlandSubheading")} </p>
            
            <label style={styles.label}> {t("mobile.referencePhoto")} </label>
            <div style={styles.uploadBox} onClick={() => document.getElementById("mobilePhotoInput").click()}>
              {preview ? <img src={preview} alt={t("mobile.referencePreview")} style={styles.previewImg} /> : <div style={styles.uploadPlaceholder}><span style={styles.uploadIcon}>📷</span><span> {t("mobile.uploadPhoto")} </span></div>}
            </div>
            <input id="mobilePhotoInput" type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: "none" }} />
            
            <label style={styles.label}> {t("mobile.neededBy")} </label>
            <div style={{ display: "flex", gap: "10px" }}>
              <input type="date" value={neededDate} onChange={(e) => setNeededDate(e.target.value)} style={{ ...styles.input, flex: 1 }} />
              <input type="text" maxLength="5" placeholder={t("mobile.egTimeHint")} value={neededTime} onChange={(e) => setNeededTime(formatTimeInput(e.target.value))} style={{ ...styles.input, flex: 1 }} />
              <select value={neededAmPm} onChange={(e) => setNeededAmPm(e.target.value)} style={{ ...styles.input, width: "78px", flexShrink: 0, padding: "12px 8px", cursor: "pointer" }}>
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
            
            <label style={styles.label}> {t("mobile.notes")} </label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("mobile.egGarlandNotes")} style={styles.textarea} />

            <hr style={styles.divider} />

            <div style={styles.noteCard}>
              <h3 style={styles.noteTitle}> {t("mobile.importantInfo")} </h3>
              <p style={styles.noteText}>
                {t("mobile.garlandNote1")}
              </p>
              <p style={styles.noteText}>
                {t("mobile.garlandNote2")}
              </p>
              <div style={styles.noteContactBox}>
                <span style={styles.noteContactLabel}> {t("mobile.supportContact")} </span>
                <span style={styles.noteContactNumber}>📞 6301912803</span>
              </div>
            </div>

            <h3 style={styles.sectionTitle}> {t("mobile.contactDelivery")} </h3>
            
            <label style={styles.label}> {t("mobile.fullName")} </label>
            <input type="text" name="fullName" value={address.fullName} onChange={handleAddressChange} placeholder={t("mobile.enterFullName")} style={styles.input} />

            <label style={styles.label}> {t("mobile.phone")} </label>
            <input type="tel" name="phone" value={address.phone} onChange={handleAddressChange} placeholder={t("mobile.egPhone")} style={styles.input} />

            <label style={styles.label}> {t("mobile.whatsapp")} </label>
            <input type="tel" name="altPhone" value={address.altPhone} onChange={handleAddressChange} placeholder={t("mobile.egPhone")} style={styles.input} />

            <label style={styles.label}> {t("mobile.buildingFlat")} </label>
            <input type="text" name="building" value={address.building} onChange={handleAddressChange} placeholder={t("mobile.egBuilding")} style={styles.input} />

            <label style={styles.label}> {t("mobile.streetColony")} </label>
            <input type="text" name="street" value={address.street} onChange={handleAddressChange} placeholder={t("mobile.egStreet")} style={styles.input} />

            <label style={styles.label}> {t("mobile.landmark")} </label>
            <input type="text" name="landmark" value={address.landmark} onChange={handleAddressChange} placeholder={t("mobile.egLandmark")} style={styles.input} />

            <div style={{ display: "flex", gap: "10px" }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}> {t("mobile.city")} </label>
                <input type="text" name="city" value={address.city} onChange={handleAddressChange} placeholder={t("mobile.egCity")} style={styles.input} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}> {t("mobile.pincode")} </label>
                <input type="text" name="postalCode" value={address.postalCode} onChange={handleAddressChange} placeholder={t("mobile.egPincode")} style={styles.input} />
              </div>
            </div>

            {error && <p style={styles.error}>{error}</p>}
            <button style={{ ...styles.submitBtn, opacity: submitting ? 0.6 : 1 }} onClick={handleSubmit} disabled={submitting}>{submitting ? t("mobile.submitting") : t("mobile.submitGarlandRequest")}</button>
          </div>
        )}
      </div>
    </div>
    </ThemeProvider>
  );
}

const styles = {
  page: { backgroundColor: COLOR.mist, minHeight: "100vh" },
  
  scrollArea: { 
    padding: "0 15px 90px", 
    boxSizing: "border-box"
  },
  
  card: { margin: "0", background: COLOR.paper, borderRadius: "14px", padding: "20px", boxShadow: SHADOW.soft },
  subheading: { color: COLOR.muted, fontSize: "14px", marginBottom: "10px", lineHeight: 1.5 },
  sectionTitle: { color: COLOR.ink, fontSize: "16px", marginTop: "0", marginBottom: "5px" },
  divider: { border: "none", borderTop: `1px solid ${COLOR.line}`, margin: "25px 0" },
  
  noteCard: {
    backgroundColor: COLOR.primarySoft,
    border: `1px solid ${COLOR.primary}`,
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "20px",
    boxShadow: SHADOW.rest
  },
  noteTitle: {
    margin: "0 0 8px 0",
    fontSize: "14px",
    fontWeight: "800",
    color: COLOR.primaryDark,
    display: "flex",
    alignItems: "center"
  },
  noteText: {
    margin: "0 0 10px 0",
    fontSize: "13px",
    color: COLOR.ink,
    lineHeight: "1.5"
  },
  noteContactBox: {
    backgroundColor: COLOR.primarySoft,
    borderRadius: "8px",
    padding: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: `1px solid ${COLOR.line}`
  },
  noteContactLabel: {
    fontSize: "12px",
    color: COLOR.primaryDark,
    fontWeight: "600"
  },
  noteContactNumber: {
    fontSize: "14px",
    fontWeight: "800",
    color: COLOR.primaryDark
  },

  label: { display: "block", fontWeight: "bold", fontSize: "13px", marginTop: "14px", marginBottom: "6px", color: COLOR.ink },
  uploadBox: { border: `2px dashed ${COLOR.line}`, borderRadius: "12px", height: "180px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", background: COLOR.surface },
  uploadPlaceholder: { display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", color: COLOR.muted },
  uploadIcon: { fontSize: "32px" },
  previewImg: { width: "100%", height: "100%", objectFit: "contain" },
  input: { width: "100%", padding: "12px", borderRadius: "8px", border: `1px solid ${COLOR.line}`, fontSize: "14px", boxSizing: "border-box" },
  textarea: { width: "100%", height: "90px", padding: "12px", borderRadius: "8px", border: `1px solid ${COLOR.line}`, fontSize: "14px", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" },
  error: { color: COLOR.error, fontSize: "14px", marginTop: "14px" },
  submitBtn: { width: "100%", marginTop: "24px", padding: "14px", border: "none", borderRadius: "10px", background: COLOR.gradientCTA ,  color: COLOR.onPrimary, fontWeight: "bold", fontSize: "16px", cursor: "pointer" },
  confirmCard: { background: COLOR.paper, borderRadius: "14px", padding: "40px 20px", textAlign: "center", boxShadow: SHADOW.soft },
  confirmIcon: { width: "60px", height: "60px", borderRadius: "50%", background: COLOR.successSoft, color: COLOR.success, fontSize: "30px", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" },
  confirmText: { color: COLOR.muted, lineHeight: 1.6, margin: "12px 0 24px" }
};